import React, { useState } from "react";
import { Button, IconButton } from "@material-ui/core";
import LightTooltip from "./LightTooltip";
import { AddCircleOutline, CancelOutlined } from "@material-ui/icons";
import { Pane, Text, HFlex, ProgressButton, CDialog, DialogSuccessNotice, useErrorContext } from "shared";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { css } from "@emotion/css";
import { SPACING, Breakpoint } from "theme";
import {
  useHealthCareOrganization,
  useCreateStripeSubscription,
  useCancelStripeSubscription,
  useStripePrices,
} from "../queries/useHealthCareOrganizations";

type DialogMode = "create" | "cancel" | null;

const HcoSubscriptionSection: React.VFC = () => {
  const { t } = useTranslation();
  const { item } = useParams<{ item: string }>();
  const { data: hco } = useHealthCareOrganization(Number(item) || 0);
  const setError = useErrorContext();
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isPerSeat = hco?.platform_fee_model === "per_seat";
  const isFlatFee = hco?.platform_fee_model === "flat_fee";
  const hasSubscription = !!hco?.stripe_subscription_id;
  const canManageSubscription = isPerSeat || isFlatFee;

  const pricesQuery = useStripePrices(hco?.id ?? 0, isPerSeat);

  const createMutation = useCreateStripeSubscription();
  const cancelMutation = useCancelStripeSubscription();

  const isLoading = createMutation.isLoading || cancelMutation.isLoading;

  const openDialog = (mode: DialogMode) => {
    setSuccessMessage(null);
    setDialogMode(mode);
  };

  const closeDialog = () => setDialogMode(null);

  const inferredPrice = pricesQuery.data?.find(p => p.unit_amount === hco?.platform_fee_cents) ?? null;

  const handleCreate = async () => {
    if (!hco) return;
    try {
      const data = isPerSeat
        ? { hcoId: hco.id, price_id: inferredPrice?.id, price_cents: inferredPrice?.unit_amount }
        : { hcoId: hco.id, price_cents: hco.platform_fee_cents ?? undefined };
      await createMutation.mutateAsync(data);
      setSuccessMessage(t("form.hco_subscription_created_successfully"));
    } catch (err) {
      setError(t("errors.could_not_create_stripe_subscription"), err);
    }
  };

  const handleCancel = async () => {
    if (!hco) return;
    try {
      await cancelMutation.mutateAsync(hco.id);
      setSuccessMessage(t("form.hco_subscription_cancelled_successfully"));
    } catch (err) {
      setError(t("errors.could_not_cancel_stripe_subscription"), err);
    }
  };

  const renderCreateConfirm = () => {
    if (successMessage) {
      return <DialogSuccessNotice heading={successMessage} body="" onClose={closeDialog} />;
    }
    const confirmMessage = (() => {
      if (isPerSeat) {
        if (!inferredPrice) return t("form.hco_create_subscription_confirm_no_price");
        const amount = (inferredPrice.unit_amount / 100).toFixed(2);
        return t("form.hco_create_subscription_confirm_per_seat", {
          amount: `${amount} ${inferredPrice.currency.toUpperCase()}`,
        });
      }
      const amount = ((hco?.platform_fee_cents ?? 0) / 100).toFixed(2);
      return t("form.hco_create_subscription_confirm_flat_fee", { amount });
    })();

    return (
      <>
        <Text heading bold mb={2}>
          {t("form.hco_create_subscription")}
        </Text>
        <Text mb={4}>{confirmMessage}</Text>
        <HFlex justifyContent="flex-end" gap={2} my={4}>
          <Button disabled={isLoading} onClick={closeDialog}>
            {t("buttons.cancel")}
          </Button>
          <ProgressButton loading={isLoading} onClick={handleCreate} disabled={pricesQuery.isLoading}>
            {t("buttons.confirm")}
          </ProgressButton>
        </HFlex>
      </>
    );
  };

  const renderCancelConfirm = () => {
    if (successMessage) {
      return <DialogSuccessNotice heading={successMessage} body="" onClose={closeDialog} />;
    }
    return (
      <>
        <Text heading bold mb={2}>
          {t("form.hco_cancel_subscription")}
        </Text>
        <Text mb={4}>{t("form.hco_cancel_subscription_confirm")}</Text>
        <HFlex justifyContent="flex-end" gap={2} my={4}>
          <Button disabled={isLoading} onClick={closeDialog}>
            {t("buttons.cancel")}
          </Button>
          <ProgressButton loading={isLoading} onClick={handleCancel}>
            {t("buttons.confirm")}
          </ProgressButton>
        </HFlex>
      </>
    );
  };

  if (!hco || !canManageSubscription) return null;

  const stripeDetails = [
    [t("form.hco_stripe_customer_id"), hco.stripe_customer_id ?? "—"],
    [t("form.hco_stripe_subscription_id"), hco.stripe_subscription_id ?? "—"],
    [t("form.hco_stripe_subscription_status"), hco.stripe_subscription_status ?? "—"],
  ];

  return (
    <>
      <Text paragraph caption secondary>
        {t("form.hco_subscription")}
      </Text>

      <Pane>
      <Pane dark mb={2}>
        <HFlex justifyContent="space-between" alignItems="flex-start">
          <div className={styles.grid}>
            {stripeDetails.map(([label, value]) => (
              <React.Fragment key={String(label)}>
                <Text light nowrap>
                  {label}
                </Text>
                <Text bold nowrap>
                  {value}
                </Text>
              </React.Fragment>
            ))}
          </div>
          {hasSubscription ? (
            <LightTooltip title={t("form.hco_cancel_subscription")}>
              <IconButton size="small" onClick={() => openDialog("cancel")} aria-label={t("form.hco_cancel_subscription")}>
                <CancelOutlined fontSize="small" style={{ color: "#d32f2f" }} />
              </IconButton>
            </LightTooltip>
          ) : (
            <LightTooltip title={t("form.hco_create_subscription")}>
              <IconButton size="small" onClick={() => openDialog("create")} aria-label={t("form.hco_create_subscription")}>
                <AddCircleOutline fontSize="small" style={{ color: "#1976d2" }} />
              </IconButton>
            </LightTooltip>
          )}
        </HFlex>
      </Pane>
      </Pane>

      <CDialog maxWidth="xs" fullWidth open={dialogMode === "create"} onClose={closeDialog}>
        <div style={{ padding: "24px" }}>{renderCreateConfirm()}</div>
      </CDialog>

      <CDialog maxWidth="xs" fullWidth open={dialogMode === "cancel"} onClose={closeDialog}>
        <div style={{ padding: "24px" }}>{renderCancelConfirm()}</div>
      </CDialog>
    </>
  );
};

export default HcoSubscriptionSection;

const styles = {
  grid: css`
    overflow: auto;
    display: grid;
    grid-auto-rows: 45px;
    align-items: center;
    grid-column-gap: ${SPACING}px;
    @media screen and (max-width: ${Breakpoint.sm - 1}px) {
      grid-template-columns: 1fr;
    }
    @media screen and (min-width: ${Breakpoint.sm}px) {
      grid-template-columns: auto 1fr;
    }
  `,
};